"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { LeftArrow, RightArrow } from "ui"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem, getUmbracoMediaUrl } from "utils"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"
import { handleAnchorScroll } from "../scrollToAnchor"
import { useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

// --- Static icons (shown only when their line has text). currentColor = card text. ---

function CityPinIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden focusable="false">
      <path
        d="M7.5 9.1665C7.5 9.82955 7.76339 10.4654 8.23223 10.9343C8.70107 11.4031 9.33696 11.6665 10 11.6665C10.663 11.6665 11.2989 11.4031 11.7678 10.9343C12.2366 10.4654 12.5 9.82955 12.5 9.1665C12.5 8.50346 12.2366 7.86758 11.7678 7.39874C11.2989 6.9299 10.663 6.6665 10 6.6665C9.33696 6.6665 8.70107 6.9299 8.23223 7.39874C7.76339 7.86758 7.5 8.50346 7.5 9.1665Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.7168 13.8806L11.1809 17.4164C10.8684 17.7286 10.4448 17.904 10.003 17.904C9.56129 17.904 9.13762 17.7286 8.82511 17.4164L5.28844 13.8806C4.35612 12.9482 3.72122 11.7603 3.46401 10.4671C3.20681 9.17394 3.33885 7.83352 3.84344 6.61536C4.34804 5.39721 5.20252 4.35604 6.29884 3.62351C7.39516 2.89098 8.68408 2.5 10.0026 2.5C11.3211 2.5 12.6101 2.89098 13.7064 3.62351C14.8027 4.35604 15.6572 5.39721 16.1618 6.61536C16.6664 7.83352 16.7984 9.17394 16.5412 10.4671C16.284 11.7603 15.6491 12.9482 14.7168 13.8806Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function BuildingIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden focusable="false">
      {[
        "M2.5 17.5H17.5",
        "M7.5 6.6665H8.33333",
        "M7.5 10H8.33333",
        "M7.5 13.3335H8.33333",
        "M11.6641 6.66699H12.4974",
        "M11.6641 10H12.4974",
        "M11.6641 13.333H12.4974",
        "M4.16406 17.5V4.16667C4.16406 3.72464 4.33966 3.30072 4.65222 2.98816C4.96478 2.67559 5.3887 2.5 5.83073 2.5H14.1641C14.6061 2.5 15.03 2.67559 15.3426 2.98816C15.6551 3.30072 15.8307 3.72464 15.8307 4.16667V17.5",
      ].map((d, i) => (
        <path
          key={i}
          d={d}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  )
}

function LinkArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden focusable="false">
      <path
        d="M13 3v4a3 3 0 0 1-3 3H3M6 7 3 10l3 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// --- Viewport entrance hook (fires once per page load) ---

function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true)
          o.disconnect()
        }
      },
      { threshold: 0.15 },
    )
    o.observe(el)
    return () => o.disconnect()
  }, [inView])
  return { ref, inView }
}

// --- Styled Components ---

const Section = styled.section`
  padding: 48px 0 64px;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 64px 0;
  }
`

/* mirrors Inner, so the carousel can line its track up with the header */
const CONTENT_MAX_WIDTH = 1440
const CONTENT_PADDING = 16
const CARD_GAP = 24

const Inner = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 20px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0 16px;
  }
`

/* title + subtitle are centered on all breakpoints (Figma header counterAxis = CENTER) */
const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;

  ${(props) => props.theme.breakpoints.up("md")} {
    margin-bottom: 64px;
  }
`

/* Rich-text title: a bold word is rendered underlined in the title colour (ASH-1520). */
const Title = styled.h2`
  font-size: 40px;
  font-weight: 500;
  line-height: 1.1;
  color: var(--comp-heading);
  margin: 0;

  p {
    margin: 0;
    display: inline;
  }
  /* emphasized word gets the hand-drawn brush underline from Figma (ASH-1797) */
  strong {
    position: relative;
    display: inline-block;
    font-weight: 700;
    text-decoration: none;
  }
  strong::after {
    content: "";
    position: absolute;
    inset-inline: 0;
    bottom: -4px;
    height: 6px;
    background-color: currentColor;
    -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
  }
`

const Subtitle = styled.p`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--comp-text);
  margin: 10px 0 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
    margin-top: 12px;
  }
`

const CarouselWrapper = styled.div`
  position: relative;

  /* let cards bleed past the container edge instead of being clipped early;
     override the global .swiper 50px vertical padding so spacing is exact */
  .swiper {
    overflow: visible;
    padding: 0;
  }

  /* equal-height slides so every card footer/link lines up */
  .swiper-wrapper {
    align-items: stretch;
  }

  /* fixed card width per Figma: 290 mobile, 335 desktop (slidesPerView="auto") */
  .swiper-slide {
    width: 290px;
    height: auto;
    display: flex;
    /* keep the fixed Figma width: without this the flex track shrinks cards
       once the offsets below leave less room than the set needs */
    flex: none;

    ${(props) => props.theme.breakpoints.up("md")} {
      width: 335px;
    }
  }
`

const Card = styled.div<{ inView: boolean; index: number }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--inner-bg);
  color: var(--inner-text);
  text-align: start;
  text-decoration: none;
  box-shadow: 0 -4px 20px 0 rgba(0, 0, 0, 0.08);

  opacity: ${({ inView }) => (inView ? 1 : 0)};
  transform: ${({ inView }) => (inView ? "translateY(0)" : "translateY(40px)")};
  transition: opacity 0.5s ease, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  transition-delay: ${({ index }) => index * 80}ms;
`

/* image is a fixed 400 tall on both breakpoints; width follows the card (335 / 290) */
const CardImage = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  flex-shrink: 0;
`

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 24px 16px;
`

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ProjectName = styled.h3`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--inner-heading, var(--inner-text));
  margin: 0;
`

/* city + secondary stay on one line (Figma NO_WRAP, gap 4) so the card body height
   is uniform (keeps the card ~552 tall on mobile too) */
const MetaRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.3;
`

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }
`

const Company = styled.p`
  font-size: 20px;
  font-weight: 400;
  line-height: 1.3;
  margin: 0;
`

const ProjectLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  font-size: 18px;
  font-weight: 400;
  color: var(--inner-text);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px dashed var(--inner-text);
    outline-offset: 2px;
  }
`

const Controls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 32px;
  }
`

const ArrowBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--comp-text);

  svg {
    color: var(--comp-text);
    path {
      fill: var(--comp-text);
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Dot = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 100px;
  padding: 0;
  cursor: pointer;
  border: ${({ active }) => (active ? "none" : "1px solid var(--comp-text)")};
  background-color: ${({ active }) => (active ? "var(--comp-text)" : "transparent")};

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

// --- Types & parsing ---

type ProjectCard = {
  imageUrl: string
  imageAlt: string
  projectName: string
  cityLine: string
  secondaryInfo: string
  operatingCompany: string
  link: { url?: string; name?: string; target?: string; type?: string } | null
}

function parseCard(block: any): ProjectCard {
  const image = extractProperty(block, "image", true) as any[] | undefined
  const link = extractProperty(block, "projectLink") as any[] | ""
  return {
    imageUrl: image?.[0]?.url || "",
    imageAlt: image?.[0]?.altText || "",
    projectName: (extractProperty(block, "projectName") as string) || "",
    cityLine: (extractProperty(block, "cityLine") as string) || "",
    secondaryInfo: (extractProperty(block, "secondaryInfo") as string) || "",
    operatingCompany: (extractProperty(block, "operatingCompany") as string) || "",
    link: (Array.isArray(link) && link[0]) || null,
  }
}

function isEmptyCard(card: ProjectCard): boolean {
  return !card.imageUrl && !card.projectName
}

// --- Main Component ---

function AdditionalProjects2({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const { ref: viewRef, inView } = useInViewOnce<HTMLDivElement>()
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [snapCount, setSnapCount] = useState(0)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const [trackOffset, setTrackOffset] = useState(0)

  const syncSwiperState = (swiper: SwiperType) => {
    setActiveIndex(swiper.activeIndex)
    setSnapCount(swiper.snapGrid.length)
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)

    /* A set that fits the viewport gets centred by centerInsufficientSlides and
       needs no offset. One that overflows scrolls instead, so it starts at the
       page gutter rather than flush against the viewport edge (ASH-2184). */
    const viewport = swiper.width
    const cardsWidth =
      swiper.slidesSizesGrid.reduce((sum, w) => sum + w, 0) +
      CARD_GAP * Math.max(swiper.slides.length - 1, 0)
    const gutter = Math.max(CONTENT_PADDING, (viewport - CONTENT_MAX_WIDTH) / 2 + CONTENT_PADDING)
    setTrackOffset(cardsWidth > viewport ? gutter : 0)
  }

  const title = extractProperty(data, "title") as string | ""
  const subtitle = extractProperty(data, "subtitle") as string | ""
  const cardBlocks = extractProperty(data, "projectCards") as any[] | ""
  const cards = (Array.isArray(cardBlocks) ? cardBlocks : [])
    .map(parseCard)
    .filter((card) => !isEmptyCard(card))

  if (!title && cards.length === 0) return null

  return (
    <Section>
      <Inner>
        <Header>
          {title && <Title dangerouslySetInnerHTML={{ __html: title }} />}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </Header>
      </Inner>

      {/* full-bleed carousel: first card flush to the RTL start (right) edge, bleeds left (ASH-1800) */}
      {cards.length > 0 && (
        <CarouselWrapper ref={viewRef}>
          <Swiper
            onSwiper={(swiper) => {
              setSwiperRef(swiper)
              syncSwiperState(swiper)
            }}
            onSlideChange={syncSwiperState}
            onResize={syncSwiperState}
            dir={isRtl ? "rtl" : "ltr"}
            spaceBetween={CARD_GAP}
            slidesPerView="auto"
            centerInsufficientSlides
            slidesOffsetBefore={trackOffset}
            slidesOffsetAfter={trackOffset}
          >
            {cards.map((card, i) => {
              const href = card.link?.url
                ? getLinkHrefFromMultiUrlPickerItem(card.link as any)
                : undefined
              const linkProps: Record<string, unknown> = href
                ? {
                    href,
                    onClick: (e: React.MouseEvent) => {
                      /* operatingCompany is the Ashtrom arm behind the card -
                         the spec's sub_company_name (ASH-1854) */
                      analytics.projectClick(card.projectName, card.operatingCompany)
                      handleAnchorScroll(e, href)
                    },
                    ...(card.link?.target ? { target: card.link.target } : {}),
                    ...(card.link?.target === "_blank" ? { rel: "noopener noreferrer" } : {}),
                  }
                : {}
              return (
                <SwiperSlide key={i} style={{ height: "auto" }}>
                  <Card as={href ? Link : "div"} inView={inView} index={i} {...linkProps}>
                    {card.imageUrl && (
                      <CardImage>
                        <Image
                          src={getUmbracoMediaUrl(card.imageUrl)}
                          fill
                          alt={card.imageAlt || card.projectName}
                          style={{ objectFit: "cover" }}
                        />
                      </CardImage>
                    )}
                    <CardBody>
                      <Info>
                        {card.projectName && <ProjectName>{card.projectName}</ProjectName>}
                        {(card.cityLine || card.secondaryInfo) && (
                          <MetaRow>
                            {card.cityLine && (
                              <MetaItem>
                                <CityPinIcon />
                                {card.cityLine}
                              </MetaItem>
                            )}
                            {card.secondaryInfo && (
                              <MetaItem>
                                <BuildingIcon />
                                {card.secondaryInfo}
                              </MetaItem>
                            )}
                          </MetaRow>
                        )}
                        {card.operatingCompany && <Company>{card.operatingCompany}</Company>}
                      </Info>
                      {card.link?.name && (
                        <ProjectLink as="span">
                          <LinkArrowIcon />
                          {card.link.name}
                        </ProjectLink>
                      )}
                    </CardBody>
                  </Card>
                </SwiperSlide>
              )
            })}
          </Swiper>

          {/* Only show nav when there is more than one page to scroll (e.g. a single card, or
                a few cards that all fit, shows no arrows/dots). */}
          {snapCount > 1 && (
            <Controls>
              <ArrowBtn
                onClick={() => swiperRef?.slidePrev()}
                disabled={isBeginning}
                aria-label={isRtl ? "לפרויקטים הקודמים" : "Previous projects"}
              >
                {isRtl ? <RightArrow fill="currentColor" /> : <LeftArrow fill="currentColor" />}
              </ArrowBtn>
              <Dots>
                {Array.from({ length: snapCount }).map((_, i) => (
                  <Dot
                    key={i}
                    active={i === activeIndex}
                    onClick={() => swiperRef?.slideTo(i)}
                    aria-label={isRtl ? `תמונה ${i + 1}` : `Image ${i + 1}`}
                  />
                ))}
              </Dots>
              <ArrowBtn
                onClick={() => swiperRef?.slideNext()}
                disabled={isEnd}
                aria-label={isRtl ? "לפרויקטים הבאים" : "Next projects"}
              >
                {isRtl ? <LeftArrow fill="currentColor" /> : <RightArrow fill="currentColor" />}
              </ArrowBtn>
            </Controls>
          )}
        </CarouselWrapper>
      )}
    </Section>
  )
}

export default AdditionalProjects2
